import {useLocation, useParams} from '@github-ui/react-router'
import {useAnalytics} from '@github-ui/use-analytics'
import {useNavigate} from '@github-ui/use-navigate'
import {useEffect, useRef, useState} from 'react'
import {issuesQueryResolver} from '../helpers/issues-query-resolver'
import {ISSUES_DASHBOARD_PATH} from '../constants/routes'

interface UseIssuesListResultsParams {
  defaultFilterValue?: string
}

interface FetchResultsParams {
  q: string
  page: number
  sidebarLinkId?: string
}

type NavigationState = {sidebarLinkId?: string}

export function useIssuesListResults({defaultFilterValue = ''}: UseIssuesListResultsParams) {
  const navigate = useNavigate()
  const location = useLocation()
  const {sendAnalyticsEvent} = useAnalytics()
  const navigationState = location.state as NavigationState | null

  const {mode} = useParams<{mode: string}>()

  const currentParams = new URLSearchParams(location.search)
  const [isEmptyQuery, setIsEmptyQuery] = useState(false)

  const query = issuesQueryResolver({searchParams: currentParams, params: {mode}})
  const fetchedQuery = isEmptyQuery ? '' : query

  const prevQueryRef = useRef(query)
  useEffect(() => {
    if (prevQueryRef.current !== query) {
      setIsEmptyQuery(false)
      prevQueryRef.current = query
    }
  }, [query])

  const currentPage = Number(currentParams.get('page') || 1)

  function fetchResults({q, page, sidebarLinkId}: FetchResultsParams) {
    sendAnalyticsEvent('issues-dashboard.search', 'LIST_RESULTS', {query: q})

    if (!q) {
      setIsEmptyQuery(true)
      return
    }

    const navigateParams = new URLSearchParams()

    if (q && q !== defaultFilterValue) {
      navigateParams.set('q', q)
    }

    if (page > 1) {
      navigateParams.set('page', page.toString())
    }
    const search = navigateParams.toString()
    const effectiveSidebarLinkId = sidebarLinkId !== undefined ? sidebarLinkId : navigationState?.sidebarLinkId
    const pathname = mode ? `${ISSUES_DASHBOARD_PATH}/${mode}` : ISSUES_DASHBOARD_PATH
    navigate(
      {
        pathname,
        search: search ? `?${search}` : '',
      },
      effectiveSidebarLinkId ? {state: {sidebarLinkId: effectiveSidebarLinkId}} : undefined,
    )
  }

  return {
    fetchedQuery,
    currentPage,
    fetchResults,
    mode,
    locationSidebarLinkId: navigationState?.sidebarLinkId ?? mode,
  }
}
